import { createSlice, createAsyncThunk, } from '@reduxjs/toolkit';
import { DateUtils, CONS } from '@pomofocus/core';
import { getApiClient } from '../../utils/apiClient';
import { globalActions } from './globalSlice';
// Async thunks
const fetchTodosOfToday = createAsyncThunk('todoList/fetchTodosOfToday', async ({ appId, onError, }, { dispatch, rejectWithValue }) => {
    try {
        dispatch(globalActions.changeVisibleModal({
            visibleModal: CONS.MODAL_TODO_LIST,
        }));
        const today = DateUtils.convertDateToYYYYMMDDWithHyphen(new Date());
        const response = await getApiClient()({
            url: '/api/todoapi/todos-of-today',
            method: 'get',
            data: { appId, today },
        });
        // Sort todos by due date and sortBy field (same logic as original)
        const todos = response.data.todos.sort((a, b) => {
            if (a.dueDate === b.dueDate) {
                return a.sortBy > b.sortBy ? 1 : -1;
            }
            if (!a.dueDate)
                return 1;
            if (!b.dueDate)
                return -1;
            return a.dueDate < b.dueDate ? -1 : 1;
        });
        return { todos };
    }
    catch (error) {
        const apiError = error;
        onError?.(apiError.message);
        const win = globalThis;
        if (win.window) {
            win.window.alert(apiError.message);
        }
        return rejectWithValue(apiError.message);
    }
});
const changeTaskState = createAsyncThunk('todoList/changeTaskState', async ({ appId, apiTaskId, isCompleted, }, { rejectWithValue }) => {
    try {
        const response = await getApiClient()({
            url: '/api/todoapi/change-task-state',
            method: 'post',
            data: { appId, apiTaskId, isCompleted },
        });
        return response.data;
    }
    catch (error) {
        const apiError = error;
        return rejectWithValue(apiError.message);
    }
});
const saveAccessToken = createAsyncThunk('todoList/saveAccessToken', async ({ appId, code, userId, onSuccess, }, { rejectWithValue }) => {
    try {
        const response = await getApiClient()({
            url: '/api/todoapi/save-access-token',
            method: 'post',
            data: { appId, code, userId },
        });
        onSuccess?.();
        return response.data;
    }
    catch (error) {
        const apiError = error;
        return rejectWithValue(apiError.message);
    }
});
const fetchTodoListAppId = createAsyncThunk('todoList/fetchTodoListAppId', async (_, { rejectWithValue }) => {
    try {
        const response = await getApiClient()({
            url: '/api/todoapi/app-id',
            method: 'get',
        });
        return response.data;
    }
    catch (error) {
        const apiError = error;
        return rejectWithValue(apiError.message);
    }
});
const removeTodolistIntegration = createAsyncThunk('todoList/removeTodolistIntegration', async (appId, { rejectWithValue }) => {
    try {
        const response = await getApiClient()({
            url: '/api/todoapi/remove-integration',
            method: 'post',
            data: { appId },
        });
        return response.data;
    }
    catch (error) {
        const apiError = error;
        return rejectWithValue(apiError.message);
    }
});
const getAllTodos = createAsyncThunk('todoList/getAllTodos', async ({ appId, onSuccess, onError, }, { rejectWithValue }) => {
    try {
        const response = await getApiClient()({
            url: '/api/todoapi/all-todos',
            method: 'get',
            data: { appId },
        });
        const todos = response.data.todos || [];
        onSuccess?.();
        return { todos };
    }
    catch (error) {
        const apiError = error;
        onError?.(apiError.message);
        return rejectWithValue(apiError.message);
    }
});
// Initial state
const initialState = {
    appId: '',
    todos: [],
    loadingTodoList: false,
    loadingAppId: false,
    loadingTaskState: false,
    loadingSaveToken: false,
    loadingRemoveIntegration: false,
    loadingAllTodos: false,
    error: null,
};
// TodoList slice
const todoListSlice = createSlice({
    name: 'todoList',
    initialState,
    reducers: {
        // Clear error state
        clearError: (state) => {
            state.error = null;
        },
        // Set app ID directly
        setAppId: (state, action) => {
            state.appId = action.payload.appId;
        },
        // Set todos directly
        setTodos: (state, action) => {
            state.todos = action.payload.todos;
        },
        // Clear todos
        clearTodos: (state) => {
            state.todos = [];
        },
        // Clear app ID (for logout/disconnect)
        clearAppId: (state) => {
            state.appId = '';
        },
        // Update specific todo in list
        updateTodo: (state, action) => {
            const todoIndex = state.todos.findIndex((todo) => todo.apiTaskId === action.payload.apiTaskId);
            if (todoIndex !== -1) {
                state.todos[todoIndex] = {
                    ...state.todos[todoIndex],
                    ...action.payload.updates,
                };
            }
        },
    },
    extraReducers: (builder) => {
        // Fetch todos of today
        builder
            .addCase(fetchTodosOfToday.pending, (state) => {
            state.loadingTodoList = true;
            state.todos = [];
            state.error = null;
        })
            .addCase(fetchTodosOfToday.fulfilled, (state, action) => {
            state.loadingTodoList = false;
            state.todos = action.payload.todos;
            state.error = null;
        })
            .addCase(fetchTodosOfToday.rejected, (state, action) => {
            state.loadingTodoList = false;
            state.error = action.payload;
        });
        // Change task state
        builder
            .addCase(changeTaskState.pending, (state) => {
            state.loadingTaskState = true;
            state.error = null;
        })
            .addCase(changeTaskState.fulfilled, (state) => {
            state.loadingTaskState = false;
            state.error = null;
        })
            .addCase(changeTaskState.rejected, (state, action) => {
            state.loadingTaskState = false;
            state.error = action.payload;
        });
        // Save access token
        builder
            .addCase(saveAccessToken.pending, (state) => {
            state.loadingSaveToken = true;
            state.error = null;
        })
            .addCase(saveAccessToken.fulfilled, (state) => {
            state.loadingSaveToken = false;
            state.error = null;
        })
            .addCase(saveAccessToken.rejected, (state, action) => {
            state.loadingSaveToken = false;
            state.error = action.payload;
        });
        // Fetch todo list app ID
        builder
            .addCase(fetchTodoListAppId.pending, (state) => {
            state.loadingAppId = true;
            state.error = null;
        })
            .addCase(fetchTodoListAppId.fulfilled, (state, action) => {
            state.loadingAppId = false;
            state.appId = action.payload.appId;
            state.error = null;
        })
            .addCase(fetchTodoListAppId.rejected, (state, action) => {
            state.loadingAppId = false;
            state.error = action.payload;
        });
        // Remove todolist integration
        builder
            .addCase(removeTodolistIntegration.pending, (state) => {
            state.loadingRemoveIntegration = true;
            state.error = null;
        })
            .addCase(removeTodolistIntegration.fulfilled, (state) => {
            state.loadingRemoveIntegration = false;
            state.appId = '';
            state.error = null;
        })
            .addCase(removeTodolistIntegration.rejected, (state, action) => {
            state.loadingRemoveIntegration = false;
            state.error = action.payload;
        });
        // Get all todos
        builder
            .addCase(getAllTodos.pending, (state) => {
            state.loadingAllTodos = true;
            state.error = null;
        })
            .addCase(getAllTodos.fulfilled, (state, action) => {
            state.loadingAllTodos = false;
            state.todos = action.payload.todos;
            state.error = null;
        })
            .addCase(getAllTodos.rejected, (state, action) => {
            state.loadingAllTodos = false;
            state.error = action.payload;
        });
    },
});
// Export individual actions
const { clearError, setAppId, setTodos, clearTodos, clearAppId, updateTodo, } = todoListSlice.actions;
// Selectors
const selectTodoListState = (state) => state.todoList;
const selectAppId = (state) => state.todoList.appId;
const selectTodos = (state) => state.todoList.todos;
const selectLoadingTodoList = (state) => state.todoList.loadingTodoList;
const selectLoadingAppId = (state) => state.todoList.loadingAppId;
const selectLoadingTaskState = (state) => state.todoList.loadingTaskState;
const selectLoadingSaveToken = (state) => state.todoList.loadingSaveToken;
const selectLoadingRemoveIntegration = (state) => state.todoList.loadingRemoveIntegration;
const selectLoadingAllTodos = (state) => state.todoList.loadingAllTodos;
const selectTodoListError = (state) => state.todoList.error;
const selectIsIntegrationConnected = (state) => Boolean(state.todoList.appId);
const selectTodosCount = (state) => state.todoList.todos.length;
const selectCompletedTodos = (state) => state.todoList.todos.filter((todo) => todo.isCompleted);
const selectPendingTodos = (state) => state.todoList.todos.filter((todo) => !todo.isCompleted);
// Grouped exports for better organization
export const todoListActions = {
    ...todoListSlice.actions,
    // Sync actions
    clearError,
    setAppId,
    setTodos,
    clearTodos,
    clearAppId,
    updateTodo,
    // Async actions
    fetchTodosOfToday,
    changeTaskState,
    saveAccessToken,
    fetchTodoListAppId,
    removeTodolistIntegration,
    getAllTodos,
};
export const todoListSelectors = {
    selectTodoListState,
    selectAppId,
    selectTodos,
    selectLoadingTodoList,
    selectLoadingAppId,
    selectLoadingTaskState,
    selectLoadingSaveToken,
    selectLoadingRemoveIntegration,
    selectLoadingAllTodos,
    selectTodoListError,
    selectIsIntegrationConnected,
    selectTodosCount,
    selectCompletedTodos,
    selectPendingTodos,
};
// Export reducer
export default todoListSlice.reducer;
